import { useEffect, useRef, useState } from "react";
import { ref, onValue, off } from "firebase/database";
import useFirebase from "hooks/useFirebase";

/**
 * Scoped replacement for reading the entire `video-calls` node.
 *
 * Reads only the signed-in user's `call-recipients/{uid}` pointer index
 * (written at call creation), keeps just the *recent* pointers, and subscribes
 * to the matching `video-calls/{callId}` nodes for live data (status / `to`).
 *
 * Result: instead of every client streaming the whole, ever-growing
 * `video-calls` collection app-wide, each client streams only its own handful
 * of recent calls. Gated on `firebaseUid` so nothing attaches before the
 * Firebase session exists (which the security rules would deny).
 */
const RECENT_WINDOW_MS = 10 * 60 * 1000; // ignore pointers older than 10 min

const useUserCalls = (): any[] => {
  const { database, firebaseUid } = useFirebase();
  const [calls, setCalls] = useState<any[]>([]);

  // Live call data keyed by callId, and the per-call listener cleanups.
  const callDataRef = useRef<Record<string, any>>({});
  const callUnsubsRef = useRef<Record<string, () => void>>({});

  useEffect(() => {
    if (!database || !firebaseUid) {
      setCalls([]);
      return;
    }

    const recipientsRef = ref(database, `call-recipients/${firebaseUid}`);

    const publish = () => {
      setCalls(Object.values(callDataRef.current).filter(Boolean));
    };

    const subscribeCall = (callId: string) => {
      if (callUnsubsRef.current[callId]) return;
      const callRef = ref(database, `video-calls/${callId}`);
      const cb = onValue(callRef, (snap) => {
        if (snap.exists()) {
          callDataRef.current[callId] = { id: callId, ...snap.val() };
        } else {
          delete callDataRef.current[callId];
        }
        publish();
      });
      callUnsubsRef.current[callId] = () => off(callRef, "value", cb);
    };

    const unsubscribeCall = (callId: string) => {
      const u = callUnsubsRef.current[callId];
      if (u) {
        u();
        delete callUnsubsRef.current[callId];
      }
      delete callDataRef.current[callId];
    };

    const recipientsCb = onValue(recipientsRef, (snap) => {
      const pointers: Record<string, any> = snap.exists() ? snap.val() : {};
      const now = Date.now();

      const wanted = new Set<string>();
      Object.entries(pointers).forEach(([callId, p]: [string, any]) => {
        const initiated = typeof p?.initiated === "number" ? p.initiated : 0;
        if (now - initiated < RECENT_WINDOW_MS) wanted.add(callId);
      });

      wanted.forEach((callId) => subscribeCall(callId));
      Object.keys(callUnsubsRef.current).forEach((callId) => {
        if (!wanted.has(callId)) unsubscribeCall(callId);
      });
      publish();
    });

    return () => {
      off(recipientsRef, "value", recipientsCb);
      Object.values(callUnsubsRef.current).forEach((u) => u());
      callUnsubsRef.current = {};
      callDataRef.current = {};
      setCalls([]);
    };
  }, [database, firebaseUid]);

  return calls;
};

export default useUserCalls;
